import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  Grid,
} from '@mui/material';
import { useAuth } from 'contexts/User';
import { useUpdateUserMutation } from 'services/GraphQl';
import { mixpanelService as mixpanel } from 'services/tracking';

interface PrivacyTermsAcceptanceModalProps {
  open: boolean;
  onAccept: () => void;
}

const PrivacyTermsAcceptanceModal: React.FC<
  PrivacyTermsAcceptanceModalProps
> = ({ open, onAccept }) => {
  const { user, refetchUserData } = useAuth();
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateUser] = useUpdateUserMutation();

  const handleAccept = async () => {
    if (!privacyAccepted || !termsAccepted) {
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      const mutationData = {
        uid: user?.uid,
        user: {
          acceptedPrivacyPolicy: new Date().toISOString(),
          acceptedTermsOfService: new Date().toISOString(),
        },
      };

      const result = await updateUser({
        variables: mutationData,
      });

      // Check if the mutation was successful
      if (result.data?.updateUser) {
        // Track the event in Mixpanel
        mixpanel.track('Updated TOS/PP Agreed', {
          userId: user?.uid,
          timestamp: new Date().toISOString(),
          privacyAccepted: true,
          termsAccepted: true,
        });

        // Manually refetch the user data using the function from UserContext
        await refetchUserData();

        // Success - close the modal
        onAccept();
      } else {
        setError('Failed to update acceptance. Please try again.');
      }
    } catch (error) {
      console.error('Error updating user acceptance:', error);
      setError(
        'An error occurred while updating your acceptance. Please try again.',
      );
    } finally {
      setUpdating(false);
    }
  };

  const canAccept = privacyAccepted && termsAccepted;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth='md'
      fullWidth
      onClose={() => {}}
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle>
        <Typography variant='h2' component='h2' gutterBottom>
          Privacy Policy &amp; Terms of Service
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity='info' sx={{ mb: 2 }}>
            <Typography variant='body2'>
              We&apos;ve updated our Privacy Policy and Terms of Service. You
              must accept both to continue using our services.
            </Typography>
          </Alert>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant='h6' gutterBottom>
              Privacy Policy
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
              Our Privacy Policy explains how we collect, use, and protect your
              personal information.
            </Typography>
            <Link
              href='https://www.forwhen.com/privacy'
              target='_blank'
              rel='noopener noreferrer'
              color='primary'
              underline='hover'
            >
              Read our Privacy Policy
            </Link>
            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyAccepted}
                  onChange={e => setPrivacyAccepted(e.target.checked)}
                  color='primary'
                />
              }
              label='I have read and agree to the Privacy Policy'
              sx={{ display: 'block', mt: 1 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant='h6' gutterBottom>
              Terms of Service
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
              Our Terms of Service outline the rules and guidelines for using
              our platform and services.
            </Typography>
            <Link
              href='https://www.forwhen.com/terms-of-service'
              target='_blank'
              rel='noopener noreferrer'
              color='primary'
              underline='hover'
            >
              Read our Terms of Service
            </Link>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  color='primary'
                />
              }
              label='I have read and agree to the Terms of Service'
              sx={{ display: 'block', mt: 1 }}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant='contained'
            color='primary'
            size='large'
            onClick={handleAccept}
            disabled={!canAccept || updating}
            sx={{ minWidth: 200 }}
          >
            {updating ? 'Updating...' : 'Accept & Continue'}
          </Button>
        </Box>

        {!canAccept && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Please accept both the Privacy Policy and Terms of Service to
              continue.
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Alert severity='error' sx={{ mb: 1 }}>
              <Typography variant='body2'>{error}</Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyTermsAcceptanceModal;
